import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Stack, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAudioDAWState } from "../../../audiodaw/react-audio-daw.js";
import { useAuth } from "../../store/auth/hooks.js";
import { useAnnotations } from "../../store/annotations/hooks.js";
import { useEditor, useEditorOperations } from "../../store/editor/hooks.js";
import { useGlimpse } from "../../store/glimpse/hooks.js";
import { useNoiseFilter } from "../../store/noiseFilter/hooks.js";
import { SERVER_URL } from "../../store/config.js";
import { getNavigationUrl } from "../../store/navigationUtil.js";
import {
  loadChannels,
  loadClip,
  queueASIFilterRequest,
  queueNLMSFilterRequest,
  queueNormalizeClip,
  queueSyncClip,
} from "./utils/dataLoader.js";
import { NOT_TAGGED } from "./utils/constants.js";
import { ContentTitleBar } from "../../components/ContentTitleBar.js";
import RegionGroupsPanel from "./RegionGroupsPanel.js";
import { ClipTitleBarMenu } from "../../components/titleBarMenu/ClipTitleBarMenu.js";
import { canEditClip } from "../../store/AclUtil.js";
import { HistoryDrawer } from "../../components/HistoryDrawer.js";
import {
  CUSTOM_THEME_LIGHT,
  EditorDAWChannelSelector,
  EditorDAWControls,
  EditorDAWEditorMenu,
} from "../../components/editor/daw";

const RULER_WIDTH = 56;
const MAIN_HEIGHT = 400;
const SELECT_WIDTH = 240;

export default function AudioDawEditorPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { caseId, clipId } = useParams();
  const { accessCode, user } = useAuth();
  const {
    isSaving: isSavingAnnotations,
    loadAnnotations,
    saveAnnotations,
  } = useAnnotations();
  const { reset: glimpseReset } = useGlimpse();
  const { clearSelection, loadPreferences, preferences, updatePreferences } =
    useEditor();
  const { cutRegion, mergeRegion } = useEditorOperations();
  const { queueNoiseFilter, reset: resetNoiseFilter } = useNoiseFilter();

  const { spectrogramVariant } = preferences;
  const canvasRef = useRef(null);
  const minimapRef = useRef(null);
  const dbCanvasRef = useRef(null);
  const frequencyCanvasRef = useRef(null);
  const loadedManifestUrlRef = useRef(null);
  const [clip, setClip] = useState(null);
  const [channel, setChannel] = useState(1);
  const [spectrogramOpacity, setSpectrogramOpacity] = useState(0);
  const [working, setWorking] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(!canEditClip(user, clip));
  const handleSetSpectrogramOpacity = (value) => setSpectrogramOpacity(value);
  const handleSetChannel = (c) => setChannel(c);
  // null until the initial load resolves, so the hydrate effect below knows
  // not to run yet.
  const [savedAnnotations, setSavedAnnotations] = useState(null);
  const SPECTRO_HEIGHT = "calc(100vh - 340px)";
  const sampleRate = clip ? clip.audioFiles[0].sampleRate : 1;

  const dispatchCut = async (navigate, clipId, tag) => {
    setWorking(true);
    setIsReadOnly(true);
    await cutRegion(caseId, clipId, tag);
    setWorking(false);
    setIsReadOnly(!canEditClip(user, clip));
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const dispatchGlimpseFilter = async (
    glimpseReset,
    navigate,
    caseId,
    clipId,
  ) => {
    await glimpseReset();
    navigate(`/app/cases/${caseId}/clips/${clipId}/glimpse/selectFiles`);
  };

  const dispatchMerge = async (tag, comment) => {
    setWorking(true);
    setIsReadOnly(true);
    await mergeRegion(caseId, clipId, tag, comment);
    setWorking(false);
    setIsReadOnly(!canEditClip(user, clip));
    navigate(`/app/cases/${caseId}/clips/`);
  };

  const dispatchNormalizeFilter = async (navigate, caseId, clipId) => {
    const result = await queueNormalizeClip(caseId, clipId);
    if (result.status === "SUCCESS") {
      navigate(`/app/cases/${caseId}/clips/${clipId}`);
    } else {
      console.log("Error normalizing clip");
    }
  };

  const runASIFilter = async (asiFilterRequest) => {
    setWorking(true);
    setIsReadOnly(true);
    console.log("[runAsiFilter] asiFilterRequest", asiFilterRequest);
    await queueASIFilterRequest(caseId, clipId, asiFilterRequest);
    setWorking(false);
    setIsReadOnly(!canEditClip(user, clip));
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const runNlmsFilter = async (nlmsFilterRequest) => {
    setWorking(true);
    setIsReadOnly(true);
    console.log("[runNlmsFilter] nlmsFilterRequest", nlmsFilterRequest);
    await queueNLMSFilterRequest(caseId, clipId, nlmsFilterRequest);
    setWorking(false);
    setIsReadOnly(!canEditClip(user, clip));
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const runSngFilter = async () => {
    setWorking(true);
    setIsReadOnly(true);
    await queueNoiseFilter(caseId, clipId, clip.commitId);
    resetNoiseFilter();
    setWorking(false);
    setIsReadOnly(!canEditClip(user, clip));
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  const runSyncfilter = async (syncFilterRequest) => {
    setWorking(true);
    setIsReadOnly(true);
    console.log("[runSyncfilter] syncFilterRequest", syncFilterRequest);
    await queueSyncClip(caseId, clipId, syncFilterRequest);
    setWorking(false);
    setIsReadOnly(!canEditClip(user, clip));
    navigate(`/app/cases/${caseId}/clips/${clipId}`);
  };

  useEffect(() => {
    // Editor preferences outlive the page, and a variant id picked on another
    // clip may not mean anything here, so start each clip on its default.
    updatePreferences({ spectrogramVariant: null });
    clearSelection();
    (async () => {
      const currentClip = await loadClip(caseId, clipId);
      if (!currentClip) {
        navigate(getNavigationUrl(caseId));
        return;
      }
      const [channels, annotationsResult] = await Promise.all([
        loadChannels(caseId, clipId),
        loadAnnotations(caseId, clipId),
        loadPreferences(caseId, clipId),
      ]);
      const activeIndex =
        channels?.findIndex((c) => c.status === "ACTIVE") ?? -1;
      setChannel(activeIndex >= 0 ? activeIndex + 1 : 1);
      setSavedAnnotations(annotationsResult.payload?.annotations ?? []);
      setClip(currentClip);
    })();
  }, [
    caseId,
    clipId,
    clearSelection,
    navigate,
    loadAnnotations,
    loadPreferences,
    updatePreferences,
  ]);

  const {
    daw,
    tracks,
    layout,
    regionGroups,
    activeRegionGroupId,
    isPlaying,
    currentTime,
    duration,
    zoom,
  } = useAudioDAWState(canvasRef, {
    minimapCanvasRef: minimapRef,
    theme: CUSTOM_THEME_LIGHT,
    // Single-track view: dragging a track to shift its start is a hazard,
    // not a feature, here.
    interactionMode: "annotate",
    // Ride through the gap while replaceTrack swaps channels, and hide the
    // momentary overlap while both tracks briefly exist.
    continuousPlayback: true,
    visibleTrackLimit: 1,
    fetchOptions: () => ({
      headers: { Authorization: `Bearer ${accessCode}` },
    }),
    regionGroupNameDisplay: "always",
    // Read through a ref by the hook, so these always see the latest render.
    // Group changes cover rename/removal, which change what gets saved.
    onRegionsChange: () => saveRegions(),
    onRegionGroupsChange: () => saveRegions(),
  });

  const activeRegionGroup =
    regionGroups.find((group) => group.id === activeRegionGroupId) ?? null;

  const trackId = tracks[0]?.id ?? null;
  const primaryTrack = tracks[0] ?? null;
  const lane = layout[0];
  const laneTop = lane?.y ?? 0;
  const laneHeight = lane?.height ?? MAIN_HEIGHT;

  const manifestUrl = clip
    ? `${SERVER_URL}/api/cases/${caseId}/clips/${clipId}/audio/${clip.commitId}/${channel}/manifest.json`
    : null;

  // First load uses addRemoteTrack; a later channel switch swaps the same
  // track live with replaceTrack instead, per the audiodaw channel-switching
  // pattern (crossfades rather than cutting out). The replacement track is
  // built on the chosen spectrogram variant, or it would come up on the
  // asset's default while the fine-tune dialog still shows the chosen one; an
  // id the new channel doesn't offer falls back to its default.
  useEffect(() => {
    if (!daw || !manifestUrl || loadedManifestUrlRef.current === manifestUrl) {
      return;
    }
    loadedManifestUrlRef.current = manifestUrl;
    if (trackId) {
      daw.replaceTrack(trackId, manifestUrl, { spectrogramVariant });
    } else {
      daw.addRemoteTrack(manifestUrl, { spectrogramVariant });
    }
  }, [daw, manifestUrl, trackId, spectrogramVariant]);

  // Carry the spectrogram opacity setting across a channel switch: the
  // replacement track starts at the library default (0) otherwise, leaving
  // the slider showing a value the picture no longer matches. Read through a
  // ref rather than depending on spectrogramOpacity directly, so this only
  // re-fires on a track swap and not on every drag tick.
  const spectrogramOpacityRef = useRef(spectrogramOpacity);
  useEffect(() => {
    spectrogramOpacityRef.current = spectrogramOpacity;
  });
  useEffect(() => {
    if (daw && trackId) {
      daw.setTrackSpectrogramOpacity(trackId, spectrogramOpacityRef.current);
    }
  }, [daw, trackId]);

  // Space toggles play/pause, but only when nothing else has claimed focus —
  // a focused text field, select, or button handles its own Space key
  // instead. This covers "don't steal Space while the user is typing in a
  // dialog" without having to track every dialog's open state.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space") return;
      if (document.activeElement && document.activeElement !== document.body) {
        return;
      }
      event.preventDefault();
      if (!daw) return;
      if (isPlaying) {
        daw.stop();
      } else {
        daw.play();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [daw, isPlaying]);

  useEffect(() => {
    if (
      !daw ||
      !trackId ||
      !dbCanvasRef.current ||
      !frequencyCanvasRef.current
    ) {
      return undefined;
    }
    daw.attachDbScale(trackId, dbCanvasRef.current, {
      tickAlign: "right",
      padding: 0,
    });
    daw.attachFrequencyScale(trackId, frequencyCanvasRef.current, {
      tickAlign: "left",
      padding: 0,
    });
    return () => {
      // The DAW can be destroyed before React runs this effect cleanup.
      // Ignore only the teardown error caused by its cleared scale manager.
      try {
        daw.detachScale(trackId);
      } catch (error) {
        if (!error?.message?.includes("detachScales")) {
          throw error;
        }
      }
    };
  }, [daw, trackId]);

  // Region group = tag: hydrate one named region group per distinct tag from
  // the saved (flat, tag-per-region) annotation list. Runs once per daw
  // instance, guarded by identity rather than a boolean so it re-hydrates if
  // the instance is ever recreated. createRegionGroup and addRegion fire the
  // region callbacks synchronously, so hydratingRef keeps those from being
  // saved straight back.
  const hydratedForDawRef = useRef(null);
  const hydratingRef = useRef(false);
  useEffect(() => {
    if (
      !daw ||
      savedAnnotations === null ||
      hydratedForDawRef.current === daw
    ) {
      return;
    }

    const byTag = {};
    for (const annotation of savedAnnotations) {
      if (!annotation.tag || annotation.tag === NOT_TAGGED) continue;
      (byTag[annotation.tag] ??= []).push(annotation);
    }
    const tags = Object.keys(byTag).sort();

    hydratingRef.current = true;
    try {
      let firstGroupId = null;
      for (const tag of tags) {
        const groupId = daw.createRegionGroup(tag);
        firstGroupId ??= groupId;
        daw.setActiveRegionGroup(groupId);
        for (const annotation of byTag[tag]) {
          daw.addRegion(annotation.start, annotation.end);
        }
      }
      if (firstGroupId) daw.setActiveRegionGroup(firstGroupId);
    } finally {
      hydratingRef.current = false;
    }
    hydratedForDawRef.current = daw;
  }, [daw, savedAnnotations]);

  // Named groups are the persisted source of truth, saved on every add,
  // drag, resize, or delete — matching the old editor's save-as-you-go
  // behavior for tagged regions. An unnamed group's regions stay local only,
  // same as an untagged region always has. Driven by the DAW's change
  // callbacks rather than the hook's `regionGroups` state: that state also
  // refreshes on every onRegionsDrag tick so lists can follow the mouse,
  // whereas onRegionsChange fires once, on release.
  const saveRegions = () => {
    if (!daw || hydratingRef.current || hydratedForDawRef.current !== daw) {
      return;
    }
    const annotationsToSave = [];
    for (const group of daw.getRegionGroups()) {
      if (!group.name) continue;
      for (const region of group.regions) {
        annotationsToSave.push({
          id: region.id,
          start: region.start,
          end: region.end,
          tag: group.name,
        });
      }
    }
    saveAnnotations(caseId, clipId, annotationsToSave);
  };

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} px={2}>
        <EditorDAWEditorMenu
          activeRegionGroup={activeRegionGroup}
          caseId={caseId}
          clipId={clipId}
          currentTime={currentTime}
          daw={daw}
          duration={duration}
          numberOfChannels={clip?.numberOfChannels}
          onAsiFilter={(formValues) => runASIFilter(formValues)}
          onCutRegion={(tag) => dispatchCut(navigate, clipId, tag)}
          onGlimpseFilter={() =>
            dispatchGlimpseFilter(glimpseReset, navigate, caseId, clipId)
          }
          onMergeRegion={(tag, comment) => dispatchMerge(tag, comment)}
          onNlmsFilter={(formValues) => runNlmsFilter(formValues)}
          onNormalizeFilter={() =>
            dispatchNormalizeFilter(navigate, caseId, clipId)
          }
          onSngFilter={() => runSngFilter()}
          readOnly={isReadOnly}
          sampleRate={sampleRate}
          spectrogramVariant={spectrogramVariant}
          onSyncFilter={(formValues) => runSyncfilter(formValues)}
          syncSelected={clip?.sync}
          trackId={trackId}
          working={working}
        />
        {/* Minimap and main/ruler canvases are always mounted:
                useAudioDAW only ever creates the instance on the render
                where canvasRef.current first exists, since a ref doesn't
                re-trigger its effect on its own. */}
        <Box display="flex" gap={2}>
          <Stack flex={1}>
            <Box
              display="flex"
              alignSelf="center"
              width={`calc(100% - ${RULER_WIDTH * 2}px)`}
              height={70}
              sx={{
                borderBottom: "1px solid",
                borderColor: theme.palette.grey[700],
              }}
            >
              <canvas
                ref={minimapRef}
                style={{ width: "100%", height: "100%" }}
              />
            </Box>
            {clip?.numberOfChannels > 1 && (
              <EditorDAWChannelSelector
                channel={channel}
                clip={clip}
                daw={daw}
                updateChannel={handleSetChannel}
                RULER_WIDTH={RULER_WIDTH}
                SELECT_WIDTH={SELECT_WIDTH}
              />
            )}
            <Box display="flex" height={SPECTRO_HEIGHT} mb={2} width={1}>
              <Box
                sx={{
                  position: "relative",
                  width: RULER_WIDTH,
                  height: SPECTRO_HEIGHT,
                }}
              >
                <canvas
                  ref={dbCanvasRef}
                  style={{
                    position: "absolute",
                    top: laneTop,
                    height: laneHeight,
                    width: "100%",
                  }}
                />
              </Box>
              <Box display="flex" flex={1}>
                <canvas
                  ref={canvasRef}
                  style={{ width: "100%", height: "100%" }}
                />
              </Box>
              <Box
                sx={{
                  position: "relative",
                  width: RULER_WIDTH,
                  height: { SPECTRO_HEIGHT },
                }}
              >
                <canvas
                  ref={frequencyCanvasRef}
                  style={{
                    position: "absolute",
                    top: laneTop,
                    height: laneHeight,
                    width: "100%",
                  }}
                />
              </Box>
            </Box>
            <EditorDAWControls
              daw={daw}
              currentTime={currentTime}
              isPlaying={isPlaying}
              zoom={zoom}
              trackId={daw?.getTracks()[0]?.id}
              spectrogramOpacity={spectrogramOpacity}
              setSpectrogramOpacity={handleSetSpectrogramOpacity}
            />
          </Stack>
          <Stack>
            <RegionGroupsPanel
              daw={daw}
              regionGroups={regionGroups}
              activeRegionGroupId={activeRegionGroupId}
              isSavingAnnotations={isSavingAnnotations}
            />
          </Stack>
        </Box>
      </Stack>
      <ContentTitleBar
        menu={
          clip ? (
            <ClipTitleBarMenu
              user={user}
              clip={clip}
              readOnly={!canEditClip(user, clip)}
              // onChange={load}
              color="light"
              navigateOnDelete
            />
          ) : null
        }
        name={clip?.description}
        label={clip?.afCase.caseName}
        onBack={() => navigate(`/app/cases/${caseId}/clips`)}
      />
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer,
        }}
      >
        <HistoryDrawer caseId={caseId} clipId={clip?.clipId} page="editor" />
      </Box>
      {!clip && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.6)",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Stack>
  );
}
